import React, { useEffect, useState } from "react";
import {
  Card,
  Badge,
  Row,
  Col,
  Form,
  Button,
  Pagination,
} from "react-bootstrap";
import theme from "../../styles/theme/theme";
import { useDispatch, useSelector } from "react-redux";
import {
  acceptReverseBid,
  fetchReverseBids,
} from "../../features/buyer/buyerSlice";
import { timeAgo } from "../../utils/timeAgo";
import { capitalizeFirst } from "../../utils/stringUtils";
import CustomAlert from "../../components/common/CustomAlert";
import Loader from "../../components/common/Loader";
import { formatCurrency, formatPrice } from "../../utils/price";

const MyRequestedBids = () => {
  const [alert, setAlert] = useState({
    message: "",
    type: "success",
    visible: false,
  });
  const dispatch = useDispatch();
  const { reverseBids, reverseBidsLoading, reverseBidsError } = useSelector(
    (state) => state.buyer
  );
  const [statusFilter, setStatusFilter] = useState("all");
  const [loadingFilter, setLoadingFilter] = useState(false);
  const PAGE_SIZE = 9;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchReverseBids()).unwrap();
  }, [dispatch]);

  const getBadgeVariant = (status) => {
    switch (status) {
      case "open":
        return "success";
      case "closed":
        return "danger";
      case "fulfilled":
        return "info";
      default:
        return "secondary";
    }
  };

  const showAlert = (message, type = "success") => {
    setAlert({ message, type, visible: true });
    setTimeout(() => {
      setAlert((prev) => ({ ...prev, visible: false }));
    }, 3000);
  };

  const handleAccept = async (id) => {
    try {
      const res = await dispatch(acceptReverseBid(id)).unwrap();
      showAlert(res.message || "Bid accepted successfully!", "success");
      dispatch(fetchReverseBids());
    } catch (error) {
      showAlert(error?.message || "Failed to accept bid.", "error");
      console.error("Error accepting bid:", error.message);
    }
  };

  // const filteredBids =
  //   statusFilter === "all"
  //     ? reverseBids || []
  //     : (reverseBids || []).filter((bid) => bid.status === statusFilter);

  if (reverseBidsLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "50vh" }}
      >
        <Loader />
      </div>
    );
  }

  //  Filter first
  const filteredBids = reverseBids.filter((bid) =>
    statusFilter === "all"
      ? true
      : bid.status.toLowerCase() === statusFilter.toLowerCase()
  );

  //  Then paginate
  const totalPages = Math.ceil(filteredBids.length / PAGE_SIZE);
  const paginatedBids = filteredBids.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  //  Pagination items
  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item
        key={number}
        active={number === currentPage}
        onClick={() => setCurrentPage(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  if (reverseBidsError)
    return <p style={{ color: "red" }}>{reverseBidsError}</p>;

  return (
    <div className="container mt-4">
      <div className="d-flex flex-nowrap justify-content-between align-items-center mb-4">
        <h3
          style={{
            color: theme.palette.text.primary,
            marginBottom: 0,
            fontSize: "1.2rem",
          }}
        >
          My Requested Bids
        </h3>
        <Form.Select
          value={statusFilter}
          onChange={(e) => {
            setLoadingFilter(true);
            setStatusFilter(e.target.value);
            setCurrentPage(1); //  reset to page 1 on filter change
            setTimeout(() => setLoadingFilter(false), 300);
          }}
          style={{
            maxWidth: 150,
            flexShrink: 0,
          }}
        >
          <option value="all">All</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
          <option value="fulfilled">Fulfilled</option>
        </Form.Select>
      </div>

      {loadingFilter ? (
        <Loader />
      ) : filteredBids.length === 0 ? (
        <div
          className="text-center py-5"
          style={{ color: theme.palette.text.secondary }}
        >
          No requested bids found for this filter.
        </div>
      ) : (
        <Row xs={1} md={2} lg={3} className="g-4">
          {paginatedBids.map((bid) => {
            const acceptedBid = bid.bids?.[0];
            return (
              <Col key={bid.id}>
                <Card className="position-relative bid-card overflow-hidden">
                  <Card.Body className="d-flex flex-column justify-content-between position-relative">
                    {/* Top-right badge and date */}
                    <div
                      style={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        textAlign: "right",
                      }}
                    >
                      <Badge bg={getBadgeVariant(bid.status)}>
                        {capitalizeFirst(bid.status)}
                      </Badge>
                      <div
                        className="text-muted mt-2"
                        style={{ fontSize: "0.75rem" }}
                      >
                        Posted: {timeAgo(bid.created_at)}
                      </div>
                    </div>

                    {/* Main Bid Details */}
                    <div className="pe-4">
                      <h6 className="mb-1">{bid.scrap_type}</h6>
                      <p
                        className="mb-1 text-muted"
                        style={{ fontSize: "0.9rem" }}
                      >
                        {bid.category?.name} / {bid.subcategory?.name}
                      </p>
                      <p className="mb-1">
                        <strong>Qty:</strong> {bid.quantity} {bid.unit}
                      </p>
                      <p className="mb-1">
                        <strong>Location:</strong> {bid.location}
                      </p>
                      <p className="mb-1">
                        <strong>Preferred Date:</strong>{" "}
                        {bid.preferred_delivery_date}
                      </p>
                      <p
                        className="mb-2"
                        style={{ fontSize: "0.85rem", color: "#666" }}
                      >
                        {bid.description}
                      </p>
                    </div>

                    {/* Expandable Seller Offer Section */}
                    <div className="seller-info px-2">
                      {acceptedBid ? (
                        <>
                          <hr style={{ margin: "1rem 0" }} />
                          <div>
                            <h6 className="mb-2">Seller Offer</h6>
                            <p className="mb-2">
                              <strong>Name: </strong>
                              {acceptedBid.seller?.name}
                            </p>
                            <p className="mb-2">
                              <strong>Company: </strong>
                              {acceptedBid.seller.company_name}
                            </p>
                            <p className="mb-2">
                              <strong>Email: </strong>
                              {acceptedBid.seller.email}
                            </p>
                            <p className="mb-2">
                              <strong>Phone: </strong>
                              {acceptedBid.seller.phone}
                            </p>
                            <p className="mb-1">
                              <strong>Offered Amount:</strong>
                              {/* {formatPrice(acceptedBid.total_amount)} */}
                              {formatCurrency(acceptedBid.total_amount,acceptedBid.currency)}
                            </p>
                          </div>
                          {/* {console.log('Bid id:',acceptedBid.id)} */}
                          {acceptedBid.status === "pending" &&
                            bid.status === "open" && (
                              <div className="d-flex justify-content-end mt-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleAccept(acceptedBid.id)}
                                >
                                  Accept Offer
                                </Button>
                              </div>
                            )}
                        </>
                      ) : (
                        <div style={{ height: "1px" }} /> // dummy to reserve space
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {!reverseBidsLoading  && !loadingFilter && totalPages > 1 && (
        <>
          <Pagination className="mt-4 justify-content-center custom-pagination">
            <Pagination.Prev
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            />
            {paginationItems}
            <Pagination.Next
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
            />
          </Pagination>
          <div
            className="text-center mt-2"
            style={{ color: theme.palette.text.secondary }}
          >
            Page {currentPage} of {totalPages}
          </div>
        </>
      )}

      {alert.visible && (
        <CustomAlert message={alert.message} type={alert.type} />
      )}

      <style>{`
        .bid-card {
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }

        .bid-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.1);
        }

        .seller-info {
          overflow: hidden;
          max-height: 0;
          opacity: 0;
          transition: max-height 0.3s ease, opacity 0.3s ease;
        }

        .bid-card:hover .seller-info {
          max-height: 300px;
          opacity: 1;
        }
      `}</style>
    </div>
  );
};

export default MyRequestedBids;
